import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Brain, Users, FileText, MessageSquare, Sparkles, Search,
  ChevronRight, Loader, Target, Star, AlertCircle, Copy,
  Building2, GraduationCap, Code, CheckCircle, XCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIRecruiterPage = () => {
  const [activeTab, setActiveTab] = useState('match');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  // Match Candidates State
  const [matchForm, setMatchForm] = useState({
    requirements: '',
    skills: [],
    max_results: 10
  });
  const [skillInput, setSkillInput] = useState('');

  // Resume Analysis State
  const [studentId, setStudentId] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);

  // Interview Questions State
  const [interviewForm, setInterviewForm] = useState({
    role: '',
    skills: [],
    experience_level: 'fresher',
    question_types: ['technical', 'behavioral'],
    count: 10
  });
  const [interviewSkillInput, setInterviewSkillInput] = useState('');
  const [questions, setQuestions] = useState([]);

  // Job Description State
  const [jdForm, setJdForm] = useState({
    title: '',
    skills: [],
    experience_level: 'fresher',
    job_type: 'full-time'
  });
  const [jdSkillInput, setJdSkillInput] = useState('');
  const [jobDescription, setJobDescription] = useState(null);

  const token = localStorage.getItem('company_token');

  const addSkill = (form, setForm, input, setInput) => {
    if (input.trim() && !form.skills.includes(input.trim())) {
      setForm({ ...form, skills: [...form.skills, input.trim()] });
      setInput('');
    }
  };

  const removeSkill = (form, setForm, skill) => {
    setForm({ ...form, skills: form.skills.filter(s => s !== skill) });
  };

  // AI Match Candidates
  const matchCandidates = async () => {
    if (!matchForm.requirements && matchForm.skills.length === 0) {
      toast.error('Please provide requirements or skills');
      return;
    }
    
    setLoading(true);
    setResults(null);
    
    try {
      const res = await axios.post(`${API}/company/ai/match-candidates`, {
        requirements: matchForm.requirements,
        skills: matchForm.skills,
        max_results: matchForm.max_results
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(res.data);
      toast.success(`Found ${res.data.matches?.length || 0} matching candidates`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to match candidates');
    } finally {
      setLoading(false);
    }
  };

  // AI Analyze Resume
  const analyzeResume = async () => {
    if (!studentId) {
      toast.error('Please enter a student ID');
      return;
    }
    
    setLoading(true);
    setAnalysisResult(null);
    
    try {
      const res = await axios.post(`${API}/company/ai/analyze-resume`, {
        student_id: studentId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysisResult(res.data);
      toast.success('Resume analyzed successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to analyze resume');
    } finally {
      setLoading(false);
    }
  };

  // AI Generate Interview Questions
  const generateQuestions = async () => {
    if (!interviewForm.role) {
      toast.error('Please enter a role');
      return;
    }
    
    setLoading(true);
    setQuestions([]);
    
    try {
      const res = await axios.post(`${API}/company/ai/generate-interview-questions`, interviewForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(res.data.questions || []);
      toast.success(`Generated ${res.data.questions?.length || 0} questions`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate questions');
    } finally {
      setLoading(false);
    }
  };

  // AI Generate Job Description
  const generateJD = async () => {
    if (!jdForm.title || jdForm.skills.length === 0) {
      toast.error('Please enter title and skills');
      return;
    }
    
    setLoading(true);
    setJobDescription(null);
    
    try {
      const res = await axios.post(`${API}/company/ai/generate-job-description`, jdForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobDescription(res.data.job_description);
      toast.success('Job description generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate JD');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'match', name: 'AI Candidate Matching', icon: Target, color: 'cyan' },
    { id: 'analyze', name: 'Resume Analysis', icon: FileText, color: 'purple' },
    { id: 'interview', name: 'Interview Questions', icon: MessageSquare, color: 'emerald' },
    { id: 'jd', name: 'Job Description', icon: Sparkles, color: 'amber' }
  ];

  if (!token) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-center">
          <Brain className="mx-auto text-purple-400 mb-4" size={64} />
          <h2 className="text-2xl font-bold mb-4">AI Recruiter</h2>
          <p className="text-gray-400 mb-6">Please login as a company to access AI features</p>
          <Link
            to="/company/auth"
            className="px-6 py-3 bg-purple-500 text-white rounded-xl font-semibold"
          >
            Company Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900/30 to-cyan-900/30 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Link to="/company/dashboard" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-4">
            ← Back to Dashboard
          </Link>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-500/20 rounded-xl">
              <Brain className="text-purple-400" size={32} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">AI Recruiter</h1>
              <p className="text-gray-400">Powered by Athena AI • Smart hiring assistant</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? `bg-${tab.color}-500/20 text-${tab.color}-400 border border-${tab.color}-500/30`
                  : 'bg-white/5 text-gray-400 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} />
              {tab.name}
            </button>
          ))}
        </div>

        {/* AI Candidate Matching */}
        {activeTab === 'match' && (
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Target className="text-cyan-400" size={24} />
                Find Best Candidates
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Requirements (describe the ideal candidate)</label>
                  <textarea
                    value={matchForm.requirements}
                    onChange={(e) => setMatchForm({ ...matchForm, requirements: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                    placeholder="E.g., Looking for a Python developer with experience in machine learning, strong problem-solving skills..."
                    data-testid="requirements-input"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Required Skills</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addSkill(matchForm, setMatchForm, skillInput, setSkillInput)}
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none"
                      placeholder="Add skill and press Enter"
                    />
                    <button
                      onClick={() => addSkill(matchForm, setMatchForm, skillInput, setSkillInput)}
                      className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {matchForm.skills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm flex items-center gap-2"
                      >
                        {skill}
                        <button onClick={() => removeSkill(matchForm, setMatchForm, skill)}>×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Max Results</label>
                  <input
                    type="number"
                    value={matchForm.max_results}
                    onChange={(e) => setMatchForm({ ...matchForm, max_results: parseInt(e.target.value) || 10 })}
                    min={1}
                    max={20}
                    className="w-24 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                  />
                </div>

                <button
                  onClick={matchCandidates}
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="match-btn"
                >
                  {loading ? <Loader className="animate-spin" size={20} /> : <Brain size={20} />}
                  {loading ? 'Analyzing...' : 'Find Matching Candidates'}
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">AI Recommendations</h3>
              
              {results?.matches?.length > 0 ? (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {results.matches.map((match, idx) => (
                    <div key={idx} className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-white font-semibold">{match.candidate?.name || 'Unknown'}</p>
                          <p className="text-gray-500 text-sm">{match.candidate?.college}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-cyan-400">{match.match_score}%</p>
                          <p className="text-xs text-gray-500">Match Score</p>
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <p className="text-xs text-gray-400 mb-1">Match Reasons:</p>
                        <div className="flex flex-wrap gap-1">
                          {match.match_reasons?.map((reason, i) => (
                            <span key={i} className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                              {reason}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {match.concerns?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-400 mb-1">Concerns:</p>
                          <div className="flex flex-wrap gap-1">
                            {match.concerns.map((concern, i) => (
                              <span key={i} className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">
                                {concern}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <Link
                        to={`/codearena/profile/${match.candidate?.username || match.student_id}`}
                        className="text-cyan-400 text-sm hover:underline"
                      >
                        View Profile →
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Target className="mx-auto mb-4 text-gray-600" size={48} />
                  <p>Enter requirements and click "Find Matching Candidates"</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Resume Analysis */}
        {activeTab === 'analyze' && (
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <FileText className="text-purple-400" size={24} />
                Analyze Candidate Profile
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Student ID</label>
                  <input
                    type="text"
                    value={studentId}
                    onChange={(e) => setStudentId(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                    placeholder="Enter student ID from talent search"
                    data-testid="student-id-input"
                  />
                </div>

                <button
                  onClick={analyzeResume}
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="analyze-btn"
                >
                  {loading ? <Loader className="animate-spin" size={20} /> : <Brain size={20} />}
                  {loading ? 'Analyzing...' : 'Analyze Profile'}
                </button>
              </div>
            </div>

            {/* Analysis Result */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">AI Analysis</h3>
              
              {analysisResult?.analysis ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <span className="text-gray-400">Overall Score</span>
                    <span className="text-3xl font-bold text-purple-400">{analysisResult.analysis.overall_score}/100</span>
                  </div>

                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-gray-400 text-sm mb-2">Recommendation</p>
                    <p className={`text-lg font-bold ${
                      analysisResult.analysis.recommendation?.includes('Strong') ? 'text-emerald-400' :
                      analysisResult.analysis.recommendation?.includes('Yes') ? 'text-cyan-400' :
                      analysisResult.analysis.recommendation?.includes('Maybe') ? 'text-amber-400' :
                      'text-rose-400'
                    }`}>
                      {analysisResult.analysis.recommendation}
                    </p>
                    <p className="text-gray-500 text-sm mt-1">{analysisResult.analysis.recommendation_reason}</p>
                  </div>

                  <div className="p-4 bg-emerald-500/10 rounded-xl">
                    <p className="text-emerald-400 text-sm mb-2">Strengths</p>
                    <ul className="space-y-1">
                      {analysisResult.analysis.strengths?.map((s, i) => (
                        <li key={i} className="text-white text-sm flex items-start gap-2">
                          <CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-4 bg-amber-500/10 rounded-xl">
                    <p className="text-amber-400 text-sm mb-2">Areas for Improvement</p>
                    <ul className="space-y-1">
                      {analysisResult.analysis.improvements?.map((s, i) => (
                        <li key={i} className="text-white text-sm flex items-start gap-2">
                          <AlertCircle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-4 bg-cyan-500/10 rounded-xl">
                    <p className="text-cyan-400 text-sm mb-2">Interview Focus Areas</p>
                    <ul className="space-y-1">
                      {analysisResult.analysis.interview_focus?.map((s, i) => (
                        <li key={i} className="text-white text-sm flex items-start gap-2">
                          <MessageSquare size={14} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="mx-auto mb-4 text-gray-600" size={48} />
                  <p>Enter a student ID to analyze their profile</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Interview Questions */}
        {activeTab === 'interview' && (
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <MessageSquare className="text-emerald-400" size={24} />
                Generate Interview Questions
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Role / Position *</label>
                  <input
                    type="text"
                    value={interviewForm.role}
                    onChange={(e) => setInterviewForm({ ...interviewForm, role: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none"
                    placeholder="E.g., Backend Developer, ML Engineer..."
                    data-testid="role-input"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Skills to Test</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={interviewSkillInput}
                      onChange={(e) => setInterviewSkillInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addSkill(interviewForm, setInterviewForm, interviewSkillInput, setInterviewSkillInput)}
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none"
                      placeholder="Add skill"
                    />
                    <button
                      onClick={() => addSkill(interviewForm, setInterviewForm, interviewSkillInput, setInterviewSkillInput)}
                      className="px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {interviewForm.skills.map((skill, idx) => (
                      <span key={idx} className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm flex items-center gap-2">
                        {skill}
                        <button onClick={() => removeSkill(interviewForm, setInterviewForm, skill)}>×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Experience Level</label>
                    <select
                      value={interviewForm.experience_level}
                      onChange={(e) => setInterviewForm({ ...interviewForm, experience_level: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                    >
                      <option value="fresher">Fresher</option>
                      <option value="1-3 years">1-3 Years</option>
                      <option value="3-5 years">3-5 Years</option>
                      <option value="5+ years">5+ Years</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Number of Questions</label>
                    <input
                      type="number"
                      value={interviewForm.count}
                      onChange={(e) => setInterviewForm({ ...interviewForm, count: parseInt(e.target.value) || 10 })}
                      min={5}
                      max={20}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Question Types</label>
                  <div className="flex flex-wrap gap-2">
                    {['technical', 'behavioral', 'coding'].map((type) => (
                      <button
                        key={type}
                        onClick={() => {
                          const types = interviewForm.question_types.includes(type)
                            ? interviewForm.question_types.filter(t => t !== type)
                            : [...interviewForm.question_types, type];
                          setInterviewForm({ ...interviewForm, question_types: types });
                        }}
                        className={`px-4 py-2 rounded-lg capitalize ${
                          interviewForm.question_types.includes(type)
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-white/5 text-gray-400'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={generateQuestions}
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="generate-questions-btn"
                >
                  {loading ? <Loader className="animate-spin" size={20} /> : <Brain size={20} />}
                  {loading ? 'Generating...' : 'Generate Questions'}
                </button>
              </div>
            </div>

            {/* Questions List */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Generated Questions</h3>
              
              {questions.length > 0 ? (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {questions.map((q, idx) => (
                    <div key={idx} className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <p className="text-white font-medium">{idx + 1}. {q.question}</p>
                        <div className="flex gap-2 flex-shrink-0">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            q.type === 'technical' ? 'bg-cyan-500/20 text-cyan-400' :
                            q.type === 'behavioral' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-amber-500/20 text-amber-400'
                          }`}>
                            {q.type}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            q.difficulty === 'easy' ? 'bg-emerald-500/20 text-emerald-400' :
                            q.difficulty === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-rose-500/20 text-rose-400'
                          }`}>
                            {q.difficulty}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-500 text-sm mb-2">Evaluates: {q.evaluates}</p>
                      {q.expected_points?.length > 0 && (
                        <div className="bg-white/5 rounded-lg p-2 mt-2">
                          <p className="text-gray-400 text-xs mb-1">Expected Answer Points:</p>
                          <ul className="text-gray-300 text-xs space-y-0.5">
                            {q.expected_points.map((point, i) => (
                              <li key={i}>• {point}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="mx-auto mb-4 text-gray-600" size={48} />
                  <p>Fill in the form and generate interview questions</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Job Description Generator */}
        {activeTab === 'jd' && (
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Sparkles className="text-amber-400" size={24} />
                Generate Job Description
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Job Title *</label>
                  <input
                    type="text"
                    value={jdForm.title}
                    onChange={(e) => setJdForm({ ...jdForm, title: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none"
                    placeholder="E.g., Senior Software Engineer..."
                    data-testid="jd-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Required Skills *</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={jdSkillInput}
                      onChange={(e) => setJdSkillInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addSkill(jdForm, setJdForm, jdSkillInput, setJdSkillInput)}
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none"
                      placeholder="Add skill"
                    />
                    <button
                      onClick={() => addSkill(jdForm, setJdForm, jdSkillInput, setJdSkillInput)}
                      className="px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {jdForm.skills.map((skill, idx) => (
                      <span key={idx} className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-lg text-sm flex items-center gap-2">
                        {skill}
                        <button onClick={() => removeSkill(jdForm, setJdForm, skill)}>×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Experience Level</label>
                    <select
                      value={jdForm.experience_level}
                      onChange={(e) => setJdForm({ ...jdForm, experience_level: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                    >
                      <option value="fresher">Fresher</option>
                      <option value="1-3 years">1-3 Years</option>
                      <option value="3-5 years">3-5 Years</option>
                      <option value="5+ years">5+ Years</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Job Type</label>
                    <select
                      value={jdForm.job_type}
                      onChange={(e) => setJdForm({ ...jdForm, job_type: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                    >
                      <option value="full-time">Full-time</option>
                      <option value="part-time">Part-time</option>
                      <option value="contract">Contract</option>
                      <option value="internship">Internship</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={generateJD}
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="generate-jd-btn"
                >
                  {loading ? <Loader className="animate-spin" size={20} /> : <Brain size={20} />}
                  {loading ? 'Generating...' : 'Generate Job Description'}
                </button>
              </div>
            </div>

            {/* Generated JD */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Generated Job Description</h3>
              
              {jobDescription ? (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  <div>
                    <h4 className="text-xl font-bold text-white mb-2">{jobDescription.title}</h4>
                    <p className="text-gray-300">{jobDescription.opening}</p>
                  </div>

                  <div>
                    <h5 className="text-amber-400 font-semibold mb-2">Key Responsibilities</h5>
                    <ul className="space-y-1">
                      {jobDescription.responsibilities?.map((r, i) => (
                        <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                          <ChevronRight size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h5 className="text-emerald-400 font-semibold mb-2">Required Qualifications</h5>
                    <ul className="space-y-1">
                      {jobDescription.required_qualifications?.map((r, i) => (
                        <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                          <CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h5 className="text-cyan-400 font-semibold mb-2">Nice to Have</h5>
                    <ul className="space-y-1">
                      {jobDescription.nice_to_have?.map((r, i) => (
                        <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                          <Star size={14} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h5 className="text-purple-400 font-semibold mb-2">What We Offer</h5>
                    <ul className="space-y-1">
                      {jobDescription.benefits?.map((r, i) => (
                        <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                          <Sparkles size={14} className="text-purple-400 mt-0.5 flex-shrink-0" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl p-4 border border-amber-500/20">
                    <p className="text-white">{jobDescription.call_to_action}</p>
                  </div>

                  <button
                    onClick={() => {
                      const text = `${jobDescription.title}\n\n${jobDescription.opening}\n\nResponsibilities:\n${jobDescription.responsibilities?.join('\n')}\n\nRequirements:\n${jobDescription.required_qualifications?.join('\n')}`;
                      navigator.clipboard.writeText(text);
                      toast.success('Job description copied!');
                    }}
                    className="w-full py-2 bg-white/10 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-white/20"
                  >
                    <Copy size={16} />
                    Copy to Clipboard
                  </button>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Sparkles className="mx-auto mb-4 text-gray-600" size={48} />
                  <p>Fill in the form to generate a job description</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIRecruiterPage;
